import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaGoogle, FaMicrosoft } from "react-icons/fa";
import logo3 from "../assets/images-webp/logo3.webp";
import { Link, useNavigate } from "react-router-dom";
import { CMSAPI, EcomAPI } from "../config";
import axios from "axios";
import { getCookie } from "../utils/cookies";
import { setCookie } from "../utils/cookies";
import toast from "react-hot-toast";
import { FaTimes } from "react-icons/fa";

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    agreeTerms: false,
  });
  const [formErrors, setFormErrors] = useState({
    email: false,
    password: false,
    agreeTerms: false,
  });
  const [apiError, setApiError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear errors when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: false,
      });
    }
    if (apiError) setApiError(null);
  };

  const validateForm = () => {
    const value = formData.email.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const isPhone = /^[0-9]{10}$/.test(value);

    const errors = {
      email: !(isEmail || isPhone),
      password: !formData.password,
      agreeTerms: !formData.agreeTerms,
    };

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await axios.get(
        `${CMSAPI}/api/Website/login-customer?email=${encodeURIComponent(
          formData.email
        )}&password=${encodeURIComponent(formData.password)}`
      );

      const data = response.data;

      if (data && data.id) {
        // Set cookies with proper expiration
        const expiryDate = new Date();
        expiryDate.setTime(expiryDate.getTime() + 24 * 60 * 60 * 1000); // 1 day
        const expires = expiryDate.toUTCString();

        document.cookie = `auth=true; expires=${expires}; path=/`;
        document.cookie = `userName=${encodeURIComponent(
          formData.email
        )}; expires=${expires}; path=/`;
        document.cookie = `email=${encodeURIComponent(
          formData.email
        )}; expires=${expires}; path=/`;
        document.cookie = `userId=${data.id}; expires=${expires}; path=/`;
        // Inside SignIn's handleSubmit
        document.cookie = `isLoggedIn=true; expires=${expires}; path=/`;

        // const expiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
        // document.cookie = `userName=${encodeURIComponent(formData.email)}; expires=${expiry}; path=/`;
        // document.cookie = `email=${encodeURIComponent(formData.email)}; expires=${expiry}; path=/`;
        // document.cookie = `isLoggedIn=true; expires=${expiry}; path=/`;
        // document.cookie = `userId=${data.id}; expires=${expiry}; path=/`;

        // MIGRATE GUEST CART ITEMS
        const guestCart = JSON.parse(getCookie("guestCart") || "[]");
        if (guestCart.length > 0) {
          try {
            // Add each guest cart item to user's cart
            for (const productId of guestCart) {
              const payload = {
                id: 0,
                productId: productId,
                userId: data.id,
              };
              await axios.post(`${CMSAPI}/api/Website/add-to-cart`, payload, {
                headers: { "Content-Type": "application/json" },
              });
            }

            // Clear guest cart after successful migration
            setCookie("guestCart", JSON.stringify([]), 86400, "/");

            // Notify user about migration
            toast.success(`${guestCart.length} item(s) moved to your cart!`);
          } catch (migError) {
            console.error("Cart migration failed:", migError);
            toast.error("Failed to migrate some cart items");
          }
        }

        // Redirect to cart page
        navigate("/cart");
      } else {
        throw new Error("Login failed: No valid response received.");
      }
    } catch (error) {
      console.error("Login error:", error);
      let errorMessage = "Login failed";

      if (error.response) {
        // Server responded with error status
        errorMessage = error.response.data?.message || "Invalid credentials";
      } else if (error.request) {
        // No response received
        errorMessage = "No response from server. Please try again.";
      } else {
        // Other errors
        errorMessage = error.message || "Login failed";
      }

      setApiError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Modal component to avoid code duplication
  const Modal = ({ title, children, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#00D084]">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>
        <div className="p-6">{children}</div>
        <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#00D084] text-white rounded hover:bg-green-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      {/* pp */}
      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <Modal
          title="Privacy Policy"
          onClose={() => setShowPrivacyModal(false)}
        >
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-0 py-10 bg-white text-gray-800 text-justify">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {/* Privacy Policy content */}
              <div className="mb-10">
                <h1 className="text-3xl font-bold text-black mb-2">
                  Privacy Policy
                </h1>
                <p className="text-lg text-black mb-4">
                  <strong>Effective Date:</strong> 17-06-2025
                  <br />
                  <strong>Platform Name:</strong> SwatchhAJV
                  <br />
                  <strong>Operated by:</strong> Ajeevi Technologies Pvt. Ltd.
                  <br />
                  <strong>Location:</strong> Noida, Uttar Pradesh, India
                </p>
              </div>

              {/* Introduction Section */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  1. Introduction
                </h4>
                <p className="text-black mb-4 text-lg">
                  Ajeevi Technologies Pvt. Ltd. ("Ajeevi", "we", "us", or "our")
                  values the trust you place in us by using our services. This
                  Privacy Policy sets forth how we collect, process, use,
                  protect, and disclose your information when you access or use
                  our SwatchhAJV Web Application, a digital civic platform
                  developed to empower citizens, municipal staff, and
                  administrators in managing Solid Waste Management (SWM)
                  operations effectively.
                </p>
                <p className="text-black text-lg">
                  SwatchhAJV is designed to improve transparency,
                  responsiveness, and sustainability in municipal waste systems.
                  We are committed to ensuring your personal information is
                  handled securely, ethically, and in accordance with applicable
                  privacy regulations, including the Digital Personal Data
                  Protection Act, 2023.
                </p>
                <p className="text-black text-lg mt-4">
                  By accessing or using SwatchhAJV, you consent to the practices
                  described in this Privacy Policy.
                </p>
              </div>

              {/* Scope of Application */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  2. Scope of Application
                </h4>
                <p className="text-black mb-4 text-lg">
                  This policy governs the processing of personal and
                  non-personal data through the SwatchhAJV platform, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>Mobile and Web Applications</li>
                  <li>APIs and integrated municipal systems</li>
                  <li>Official support or helpdesk tools</li>
                  <li>SMS/Email notifications and push alerts</li>
                </ul>
                <p className="text-black text-lg mb-4">
                  It applies to all users, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>Residents of India availing SWM services</li>
                  <li>
                    Municipal employees performing administrative or field
                    duties
                  </li>
                  <li>
                    Authorized contractors and operators appointed by civic
                    bodies
                  </li>
                  <li>
                    Other individuals interacting with the platform in any
                    capacity
                  </li>
                </ul>
                <p className="text-black text-lg">
                  If you are accessing the platform on behalf of a third party
                  (e.g., as a contractor or municipal staff), you represent that
                  you have the authority to bind that entity and that you
                  consent to this Privacy Policy on their behalf.
                </p>
              </div>

              {/* Types of Information We Collect */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  3. Types of Information We Collect
                </h4>
                <p className="text-black mb-4 text-lg">
                  We may collect various types of information, either directly
                  from you or automatically during your use of the platform.
                </p>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  3.1 Personally Identifiable Information (PII)
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>Full Name (First Name, Last Name)</li>
                  <li>
                    Mobile Phone Number (used for OTP login and notifications)
                  </li>
                  <li>Email Address (if voluntarily provided)</li>
                  <li>
                    Aadhaar or government-issued ID (optional; collected only
                    for verification if authorized)
                  </li>
                  <li>Home or GPS Location (with explicit user permission)</li>
                  <li>Complaint Reference ID and Linked History</li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  3.2 Device and Technical Metadata
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>IP Address and Geographic Region</li>
                  <li>Device Type and OS Version</li>
                  <li>Browser Name, Version, and Language Preferences</li>
                  <li>
                    IMEI or Device Identifiers (only for authorized field
                    devices)
                  </li>
                  <li>
                    Mobile network data or Wi-Fi logs (for route validation)
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  3.3 Civic Complaint and Service Use Data
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>
                    Nature and Category of Complaint (garbage pickup, missed
                    service, etc.)
                  </li>
                  <li>
                    Media Uploads: Photos, videos, or supporting documents
                  </li>
                  <li>Date, Time, and Resolution Status of Complaints</li>
                  <li>
                    Location mapping of the complaint (either manually entered
                    or GPS tagged)
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  3.4 Behavioral and Usage Data
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>Login and logout timestamps</li>
                  <li>Number of complaints registered/viewed</li>
                  <li>
                    Modules accessed (e.g., Complaint Tracker, Vehicle Route,
                    Collection Schedule)
                  </li>
                  <li>User feedback or satisfaction rating data</li>
                  <li>Search queries and platform navigation patterns</li>
                </ul>
              </div>

              {/* How We Use Your Data */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  4. How We Use Your Data
                </h4>
                <p className="text-black mb-4 text-lg">
                  We collect and use your information for multiple civic and
                  service-related purposes, including:
                </p>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  4.1 Platform Functionality
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>To authenticate and register users securely</li>
                  <li>
                    To validate complaints and assign them to the correct civic
                    ward or division
                  </li>
                  <li>
                    To notify users on complaint status, route changes, or
                    service updates
                  </li>
                  <li>
                    To ensure accurate geolocation for field verification and
                    service mapping
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  4.2 Municipal Service Efficiency
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>
                    To optimize garbage vehicle routes using live and historical
                    data
                  </li>
                  <li>
                    To reduce service duplication and prevent resource misuse
                  </li>
                  <li>
                    To enable data-driven municipal planning and policy
                    formulation
                  </li>
                  <li>
                    To generate monthly or quarterly analytical dashboards for
                    authorities
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  4.3 Civic Engagement
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>
                    To conduct surveys or feedback collection on municipal
                    cleanliness
                  </li>
                  <li>
                    To identify areas with recurring complaints and escalate for
                    intervention
                  </li>
                  <li>
                    To share educational updates or sanitation campaigns via
                    alerts
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  4.4 Legal and Compliance
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    To ensure legal compliance with Indian data protection laws
                  </li>
                  <li>To investigate misuse or abuse of the platform</li>
                  <li>
                    To comply with audit trails, government inspections, or
                    legal summons
                  </li>
                </ul>
              </div>

              {/* Legal Grounds for Processing */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  5. Legal Grounds for Processing (As per Indian Law)
                </h4>
                <p className="text-black mb-4 text-lg">
                  We rely on the following legal bases:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>
                    <strong>Consent:</strong> When you voluntarily provide data
                    or submit a complaint
                  </li>
                  <li>
                    <strong>Contractual necessity:</strong> When municipal staff
                    access data to deliver public services
                  </li>
                  <li>
                    <strong>Public duty:</strong> To enhance sanitation, health,
                    and governance
                  </li>
                  <li>
                    <strong>Legal obligation:</strong> Where the law mandates
                    data retention or sharing
                  </li>
                  <li>
                    <strong>Legitimate interest:</strong> To improve municipal
                    technology and service delivery
                  </li>
                </ul>
              </div>

              {/* Data Sharing and Disclosure */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  6. Data Sharing and Disclosure
                </h4>
                <p className="text-black mb-4 text-lg">
                  We do not sell, rent, or share your data for profit. However,
                  we may share information under the following strictly
                  controlled scenarios:
                </p>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  6.1 Government and Municipal Authorities
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>For resolving submitted grievances</li>
                  <li>For real-time service tracking and planning</li>
                  <li>
                    For district/state-level sanitation reports and reviews
                  </li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  6.2 Technology and Support Partners
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>
                    Hosting providers, SMS gateways, and map integration vendors
                  </li>
                  <li>
                    Customer support vendors (for IVR, helpdesk, ticketing)
                  </li>
                  <li>IT security auditors and consultants (under NDAs)</li>
                </ul>

                <h5 className="text-xl font-semibold mb-3 text-black">
                  6.3 Law Enforcement and Compliance
                </h5>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>Where mandated by a court order, warrant, or statute</li>
                  <li>
                    In response to legal claims, fraud, or policy violations
                  </li>
                </ul>
                <p className="text-black text-lg mt-4">
                  All third parties are bound by confidentiality agreements and
                  data handling clauses in compliance with Indian laws.
                </p>
              </div>

              {/* Data Security and Protection */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  7. Data Security and Protection
                </h4>
                <p className="text-black mb-4 text-lg">
                  We use a combination of technical, administrative, and
                  organizational safeguards to protect your data, including:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>End-to-end SSL/TLS encryption of communications</li>
                  <li>Role-based user access and authentication with OTPs</li>
                  <li>Centralized logging and suspicious activity detection</li>
                  <li>Data backups and disaster recovery protocols</li>
                  <li>
                    Server-side firewalls, IP whitelisting, and rate limiting
                  </li>
                </ul>
                <p className="text-black text-lg mb-4">
                  We continually assess risks and update our defenses. Still, no
                  system is fully invulnerable. You are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>Keeping OTPs and passwords confidential</li>
                  <li>Logging out of shared devices</li>
                  <li>Reporting any suspicious activity to our support team</li>
                </ul>
              </div>

              {/* Data Retention Policy */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  8. Data Retention Policy
                </h4>
                <p className="text-black mb-4 text-lg">
                  We follow a structured data retention and archival policy:
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 border border-gray-200">
                          Data Type
                        </th>
                        <th className="px-4 py-2 border border-gray-200">
                          Retention Duration
                        </th>
                        <th className="px-4 py-2 border border-gray-200">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-2 border border-gray-200">
                          Complaint Records
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          5 years minimum
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          Government audit & traceability
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border border-gray-200">
                          User Profiles
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          Until deactivation or 2 years of inactivity
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          Operational reference
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border border-gray-200">
                          GPS/Vehicle Logs
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          12 months
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          Optimization & performance
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border border-gray-200">
                          System Logs
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          90–180 days
                        </td>
                        <td className="px-4 py-2 border border-gray-200">
                          Security and compliance
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-black text-lg mt-4">
                  Once the retention period ends, the data is securely deleted
                  or anonymized.
                </p>
              </div>

              {/* Cookies and Digital Identifiers */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  9. Cookies and Digital Identifiers
                </h4>
                <p className="text-black mb-4 text-lg">
                  SwatchhAJV uses session-based cookies and browser local
                  storage to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>Maintain logged-in sessions</li>
                  <li>Store location pin data (if permitted)</li>
                  <li>Optimize app responsiveness</li>
                </ul>
                <p className="text-black text-lg mt-4">
                  We do not use tracking cookies for advertisements, social
                  media, or third-party analytics.
                </p>
              </div>

              {/* Your Rights as a User */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  10. Your Rights as a User
                </h4>
                <p className="text-black mb-4 text-lg">
                  In accordance with Indian data protection norms, you are
                  entitled to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>
                    <strong>Right to Access:</strong> Obtain a copy of your
                    stored data
                  </li>
                  <li>
                    <strong>Right to Correction:</strong> Request edits to
                    incorrect or outdated information
                  </li>
                  <li>
                    <strong>Right to Withdraw Consent:</strong> Revoke
                    permissions for further data processing
                  </li>
                  <li>
                    <strong>Right to Erasure:</strong> Request deletion of your
                    personal data (subject to legal limitations)
                  </li>
                  <li>
                    <strong>Right to Grievance Redressal:</strong> File
                    complaints with our Data Protection Officer (DPO)
                  </li>
                </ul>
                <p className="text-black text-lg">
                  Please send an email to{" "}
                  <a
                    href="mailto:support@ajeevi.com"
                    className="text-[#00D084] hover:underline"
                  >
                    support@ajeevi.com
                  </a>{" "}
                  to exercise any of these rights.
                </p>
              </div>

              {/* Children's Privacy */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  11. Children's Privacy
                </h4>
                <p className="text-black text-lg">
                  SwatchhAJV is not intended for children under 13. We do not
                  knowingly collect personal data from minors. If you believe a
                  child has used the service without parental consent, notify us
                  immediately and we will promptly delete the associated
                  records.
                </p>
              </div>

              {/* Third-Party Integrations */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  12. Third-Party Integrations
                </h4>
                <p className="text-black mb-4 text-lg">
                  We may embed or redirect to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black">
                  <li>Municipal corporation websites</li>
                  <li>Government sanitation portals</li>
                  <li>Google Maps or GIS-based services</li>
                </ul>
                <p className="text-black text-lg mt-4">
                  We are not responsible for the privacy policies or security
                  practices of external services. Users are advised to review
                  such policies independently.
                </p>
              </div>

              {/* Updates to this Privacy Policy */}
              <div className="mb-10">
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  13. Updates to this Privacy Policy
                </h4>
                <p className="text-black mb-4 text-lg">
                  This policy may be updated:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                  <li>In response to legal or regulatory changes</li>
                  <li>When we expand platform functionality</li>
                  <li>Based on user feedback or risk assessments</li>
                </ul>
                <p className="text-black text-lg">
                  Users will be notified via platform banner, email, or push
                  notification. Continued use of the platform after the update
                  constitutes acceptance of the revised terms.
                </p>
              </div>

              {/* Contact and Grievance Officer */}
              <div>
                <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                  14. Contact and Grievance Officer
                </h4>
                <p className="text-black mb-4 text-lg">
                  For questions, grievances, or support:
                </p>
                <p className="text-black text-lg mb-2">
                  <strong>Ajeevi Technologies Pvt. Ltd.</strong>
                  <br />
                  505, Advant Navis Business Park, Tower A-1
                  <br />
                  Corporate Park, Sector 142
                  <br />
                  Noida, Uttar Pradesh – 201301
                </p>
                <p className="text-black text-lg">
                  📧 Email:{" "}
                  <a
                    href="mailto:support@ajeevi.com"
                    className="text-[#00D084] hover:underline"
                  >
                    support@ajeevi.com
                  </a>
                  <br />
                  📞 Phone:{" "}
                  <a
                    href="tel:+919654325300"
                    className="text-[#00D084] hover:underline"
                  >
                    +91-9654325300
                  </a>
                  <br />
                  🌐 Website:{" "}
                  <a
                    href="https://swachhajv.ajeevi.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#00D084] hover:underline"
                  >
                    https://swachhajv.ajeevi.in
                  </a>
                </p>
                <p>Note: Privacy Policy is subjected to changed anytime.</p>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {/* pp */}

      {/* TOU */}
      {/* Terms of Use Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#00D084]">
                Terms of Use
              </h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>
            <div className="p-6">
              <div className="w-full px-4 sm:px-6 md:px-10 lg:px-0 py-10 bg-white text-gray-800 text-justify">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                  {/* Header Section */}
                  <div className="mb-10">
                    <h1 className="text-3xl font-bold text-black mb-2">
                      Terms of Use
                    </h1>
                    <p className="text-lg text-black mb-4">
                      <strong>Effective Date:</strong> 17-06-2025
                      <br />
                      <strong>Platform:</strong> SwachhAJV
                      <br />
                      <strong>Operated By:</strong> Ajeevi Technologies Pvt.
                      Ltd.
                    </p>
                  </div>

                  {/* Welcome Statement */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      Welcome Statement
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      Welcome to SwatchhAJV, a digital platform developed and
                      maintained by Ajeevi Technologies Pvt. Ltd. for the
                      purpose of enabling smart, efficient, and citizen-centric
                      Solid Waste Management (SWM) services. This application
                      aims to transform waste management practices through
                      real-time monitoring, grievance redressal, data-driven
                      operations, and active public engagement.
                    </p>
                    <p className="text-black text-lg">
                      These Terms of Use govern your access to and use of this
                      platform, including any associated services, tools,
                      interfaces, and content. By using this platform, you
                      accept and agree to be legally bound by these terms, and
                      any applicable laws, regulations, and policies.
                    </p>
                  </div>

                  {/* Purpose of the Platform */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      1. Purpose of the Platform
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      The SwatchhAJV application serves as a comprehensive SWM
                      ecosystem, intended to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Empower citizens to report waste management issues and
                        receive timely resolutions.
                      </li>
                      <li>
                        Enable authorities to track waste vehicles and bin
                        collection schedules in real-time.
                      </li>
                      <li>
                        Facilitate analytics-based planning and resource
                        allocation using collected data.
                      </li>
                      <li>
                        Enhance accountability through transparent service
                        tracking and complaint dashboards.
                      </li>
                      <li>
                        Improve public awareness via timely announcements,
                        service updates, and emergency alerts.
                      </li>
                      <li>
                        Digitize municipal operations for better governance,
                        automation, and communication.
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      The platform plays a crucial role in supporting the Smart
                      Cities Mission, Swachh Bharat Abhiyan, and other digital
                      sanitation initiatives in India.
                    </p>
                  </div>

                  {/* Eligibility and Access */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      2. Eligibility and Access
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      Access to the platform is permitted only to eligible
                      users:
                    </p>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      2.1 Authorized Users
                    </h5>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Citizens of India, residing within the service
                        boundaries of partnered Urban Local Bodies (ULBs) or
                        municipalities.
                      </li>
                      <li>
                        Government officials involved in SWM operations and
                        monitoring.
                      </li>
                      <li>
                        Contractors and Operators officially appointed to carry
                        out field activities such as waste collection,
                        segregation, and transfer.
                      </li>
                    </ul>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      2.2 Restricted Users
                    </h5>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>Individuals attempting to impersonate others.</li>
                      <li>
                        Users who have previously been suspended or banned for
                        violating these terms.
                      </li>
                      <li>
                        Bots, crawlers, or other automated systems not
                        authorized by the administrator.
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      Use of the application by unauthorized users or for
                      unauthorized purposes is strictly prohibited and may be
                      reported to the appropriate authorities.
                    </p>
                  </div>

                  {/* User Responsibilities and Conduct */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      3. User Responsibilities and Conduct
                    </h4>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      3.1 Data Accuracy
                    </h5>
                    <p className="text-black mb-4 text-lg">
                      Users must ensure that any data, complaint, request, or
                      feedback submitted through the platform is accurate,
                      relevant, and timely. False complaints, misleading
                      information, or inappropriate uploads will result in
                      account suspension and legal accountability.
                    </p>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      3.2 Ethical Use
                    </h5>
                    <p className="text-black mb-2 text-lg">Users agree to:</p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Act respectfully toward sanitation staff, officials, and
                        other users.
                      </li>
                      <li>
                        Refrain from using offensive, abusive, or discriminatory
                        language.
                      </li>
                      <li>
                        Avoid uploading images or videos that violate decency
                        norms or contain harmful content.
                      </li>
                      <li>
                        Use the application only for its intended civic purpose.
                      </li>
                    </ul>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      3.3 Security and Confidentiality
                    </h5>
                    <p className="text-black mb-2 text-lg">
                      Users are solely responsible for:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Securing their login credentials and avoiding
                        unauthorized sharing.
                      </li>
                      <li>
                        Logging out from shared or public devices after use.
                      </li>
                      <li>
                        Reporting any suspicious activity or unauthorized access
                        to the platform support team immediately.
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      Any misuse of administrative access or unauthorized data
                      handling may result in both criminal and civil
                      proceedings.
                    </p>
                  </div>

                  {/* Platform Functionality and Limitations */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      4. Platform Functionality and Limitations
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      While we aim to provide a consistent and reliable user
                      experience, the following limitations apply:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        <strong>Real-Time Data Lag:</strong> GPS and IoT-based
                        tracking systems may experience delays depending on
                        connectivity and device calibration.
                      </li>
                      <li>
                        <strong>Device Compatibility:</strong> Some features may
                        not be fully optimized for all browsers or devices,
                        particularly outdated mobile systems.
                      </li>
                      <li>
                        <strong>Notification Delivery:</strong> Alerts sent via
                        SMS/email are subject to third-party network delivery
                        success and may be delayed.
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      Ajeevi Technologies Pvt. Ltd. is not liable for
                      inaccuracies caused by third-party hardware, network
                      outages, or force majeure events.
                    </p>
                  </div>

                  {/* Data Collection, Use, and Protection */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      5. Data Collection, Use, and Protection
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      Your privacy is a priority. We collect personal, usage,
                      and technical information strictly for service improvement
                      and statutory reporting.
                    </p>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      5.1 Types of Data Collected
                    </h5>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        <strong>Personal identifiers:</strong> Name, phone
                        number, email address
                      </li>
                      <li>
                        <strong>Location data:</strong> GPS coordinates (for
                        complaint mapping or vehicle interaction)
                      </li>
                      <li>
                        <strong>Technical metadata:</strong> IP address, device
                        type, session logs
                      </li>
                      <li>
                        <strong>Uploaded content:</strong> Images or documents
                        submitted with grievances
                      </li>
                    </ul>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      5.2 Data Usage
                    </h5>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Service optimization, complaint routing, and analytics
                      </li>
                      <li>
                        Public reporting (anonymized) to measure sanitation
                        impact
                      </li>
                      <li>
                        Audit trails for service improvement and transparency
                      </li>
                      <li>
                        Legal compliance as mandated by local/state regulations
                      </li>
                    </ul>

                    <h5 className="text-xl font-semibold mb-3 text-black">
                      5.3 Data Protection
                    </h5>
                    <p className="text-black mb-2 text-lg">We employ:</p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>SSL encryption</li>
                      <li>Secure database storage</li>
                      <li>Role-based access</li>
                      <li>Regular vulnerability assessments</li>
                      <li>Privacy-aware architecture</li>
                    </ul>
                    <p className="text-black text-lg">
                      We will not sell, rent, or trade your data to third
                      parties. Your data may be shared with governmental
                      authorities only if required under law.
                    </p>
                  </div>

                  {/* Acceptable Use Policy */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      6. Acceptable Use Policy
                    </h4>
                    <p className="text-black mb-2 text-lg">
                      You are prohibited from:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>Interfering with system integrity or security</li>
                      <li>
                        Reverse-engineering, modifying, or creating derivative
                        works from platform components
                      </li>
                      <li>
                        Transmitting viruses, malware, or other harmful code
                      </li>
                      <li>
                        Conducting any activity that threatens the availability
                        or performance of the platform
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      Users in violation will face immediate suspension, and
                      their activities may be reported to law enforcement
                      authorities.
                    </p>
                  </div>

                  {/* Ownership and Intellectual Property */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      7. Ownership and Intellectual Property
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      All intellectual property rights associated with
                      SwatchhAJV, including but not limited to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>Source code</li>
                      <li>Visual elements</li>
                      <li>Data models and schemas</li>
                      <li>Platform branding</li>
                      <li>Analytics dashboards and reporting logic</li>
                    </ul>
                    <p className="text-black text-lg">
                      are the exclusive property of Ajeevi Technologies Pvt.
                      Ltd., unless otherwise stated. Unauthorized copying, use,
                      or redistribution is strictly prohibited and may lead to
                      civil or criminal action.
                    </p>
                  </div>

                  {/* Termination and Suspension */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      8. Termination and Suspension
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      Ajeevi Technologies Pvt. Ltd. reserves the right to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        Suspend user accounts that violate platform policies or
                        show suspicious behavior
                      </li>
                      <li>
                        Restrict access to features based on user type (citizen,
                        admin, contractor)
                      </li>
                      <li>
                        Delete or archive data that is malicious, abusive, or
                        deemed harmful to public order
                      </li>
                    </ul>
                    <p className="text-black text-lg">
                      Repeated offenses may result in permanent blacklisting of
                      the user or legal escalation.
                    </p>
                  </div>

                  {/* Modifications and Amendments */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      9. Modifications and Amendments
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      We may revise these Terms of Use to reflect changes in:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>Legal frameworks</li>
                      <li>Technological advancements</li>
                      <li>Service offerings or scope of work</li>
                      <li>Platform architecture</li>
                    </ul>
                    <p className="text-black text-lg">
                      When such changes occur, we will notify users via in-app
                      announcements, banners, or email. Continued use of the
                      platform after changes implies acceptance of the updated
                      terms.
                    </p>
                  </div>

                  {/* Disclaimers */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      10. Disclaimers
                    </h4>
                    <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
                      <li>
                        <strong>No Warranty:</strong> The platform is provided
                        on an "as-is" and "as-available" basis without
                        warranties of any kind.
                      </li>
                      <li>
                        <strong>No Liability:</strong> Ajeevi shall not be
                        liable for any incidental, indirect, or consequential
                        damages including data loss, service disruption, or user
                        dissatisfaction.
                      </li>
                      <li>
                        <strong>Government Contracts:</strong> Municipal bodies
                        using the platform are responsible for their respective
                        jurisdictional policies and service agreements.
                      </li>
                    </ul>
                  </div>

                  {/* Governing Law and Dispute Resolution */}
                  <div className="mb-10">
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      11. Governing Law and Dispute Resolution
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      These Terms of Use shall be governed by and construed in
                      accordance with the laws of the State of Uttar Pradesh,
                      India. Any disputes arising under these terms shall be
                      exclusively subject to the jurisdiction of courts located
                      in Noida, Uttar Pradesh.
                    </p>
                    <p className="text-black text-lg">
                      In the event of any legal dispute, the user agrees to
                      attempt informal resolution via the platform support team
                      before initiating legal proceedings.
                    </p>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
                      12. Contact Information
                    </h4>
                    <p className="text-black mb-4 text-lg">
                      For technical support, grievance handling, account-related
                      inquiries, or legal matters, you may reach out to:
                    </p>
                    <p className="text-black text-lg mb-2">
                      <strong>Ajeevi Technologies Pvt. Ltd.</strong>
                      <br />
                      505, Advant Navis Business Park, Tower A-1
                      <br />
                      Corporate Park, Sector 142, Noida
                      <br />
                      Uttar Pradesh – 201301, India
                    </p>
                    <p className="text-black text-lg">
                      📧 Email:{" "}
                      <a
                        href="mailto:support@ajeevi.com"
                        className="text-[#00D084] hover:underline"
                      >
                        support@ajeevi.com
                      </a>
                      <br />
                      📞 Phone:{" "}
                      <a
                        href="tel:+919654325300"
                        className="text-[#00D084] hover:underline"
                      >
                        +91-9654325300
                      </a>
                      <br />
                      🌐 Website:{" "}
                      <a
                        href="https://swachhajv.ajeevi.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00D084] hover:underline"
                      >
                        https://swachhajv.ajeevi.in
                      </a>
                    </p>
                    <p>
                      Note: Terms & Conditions is subjected to changed anytime.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-4 border-t flex justify-end">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2 bg-[#00D084] text-white rounded hover:bg-green-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* TOU */}

      <div className="w-full max-w-4xl bg-white rounded-lg shadow-md overflow-hidden">
        <FaTimes
          className="text-gray-500 cursor-pointer text-2xl ml-auto mt-1 mr-1"
          onClick={() => navigate("/")}
        />
        <div className="flex flex-col md:flex-row">
          {/* Left Section */}
          <div className="hidden md:flex md:w-1/2 bg-white flex-col justify-center items-center p-8 text-center">
            <img
              src={logo3}
              alt="SwachhAjv Logo"
              className="max-w-[150px] mb-4"
            />
            <h5 className="font-bold text-lg">
              Welcome to AI Enabled Waste Solutions
            </h5>
            <p className="text-gray-500 text-md mt-2">
              Your AI enabled waste solution for waste collection, tracking, and
              monitoring. Log in to manage your waste management dashboard
              efficiently.
            </p>
            <Link
              to="/"
              className="mt-4 px-2 py-1 border border-[#00D084] text-[#00D084] rounded-lg text-md hover:bg-[#00D084] hover:text-white transition-colors"
            >
              Go to Home
            </Link>
          </div>

          {/* Right Section */}
          <div className="w-full md:w-1/2 p-6 md:p-8">
            <div className="text-center mb-6">
              <h5 className="font-bold text-[#00D084] mb-1 text-xl">Sign In</h5>
              <p className="text-gray-500 text-md">
                Access your SwachhAJV dashboard
              </p>
            </div>

            {/* API Error Message */}
            {apiError && (
              <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Error:</span>
                </div>
                <p className="mt-1 text-sm">{apiError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-md text-gray-600 mb-1"
                >
                  Email or Mobile
                </label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md text-sm ${
                    formErrors.email ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your email or mobile number"
                  required
                />
                {formErrors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    Please enter a valid email or 10-digit mobile number.
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-md text-gray-600 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md text-sm ${
                      formErrors.password ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your Password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">
                    Password is required.
                  </p>
                )}
              </div>

              {/* Forgot Password */}
              <div className="flex justify-end">
                {/* <Link 
                  to="/forgot-password" 
                  className="text-md text-[#00D084] hover:underline"
                >
                  Forgot password?
                </Link> */}
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  className="mt-1 mr-2"
                  required
                />
                <label htmlFor="agreeTerms" className="text-sm text-gray-600">
                  By continuing, I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-[#00D084] font-semibold hover:underline"
                  >
                    Terms of Use
                  </button>{" "}
                  &{" "}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-red-500 font-semibold hover:underline"
                  >
                    Privacy Policy
                  </button>{" "}
                  and I am above 18 years old.
                </label>
                {formErrors.agreeTerms && (
                  <p className="text-red-500 text-xs mt-1">
                    You must agree to the terms.
                  </p>
                )}
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                className="w-full bg-[#00D084] text-white py-2 rounded-md hover:bg-green-600 transition-colors disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing In...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>

              {/* OR Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-2 text-sm text-gray-500">
                    OR
                  </span>
                </div>
              </div>

              {/* Social Buttons */}
              {/* <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 flex items-center justify-center gap-2 border border-red-500 text-red-500 py-2 rounded-md hover:bg-red-50 transition-colors text-sm"
                >
                  <FaGoogle /> Google
                </button>
                <button
                  type="button"
                  className="flex-1 flex items-center justify-center gap-2 border border-blue-500 text-blue-500 py-2 rounded-md hover:bg-blue-50 transition-colors text-sm"
                >
                  <FaMicrosoft /> Microsoft
                </button>
              </div> */}

              {/* Create Account */}
              <div className="text-center text-sm">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="text-[#00D084] font-medium hover:underline"
                >
                  Create an account
                </Link>
              </div>

              {/* Get Help */}
              {/* <div className="text-center text-sm mt-2">
                Have trouble logging in?{' '}
                <Link to="/help" className="text-red-500 font-medium hover:underline">
                  Get help
                </Link>
              </div> */}
            </form>
          </div>
        </div>
      </div>
    </div> 
  );
};

export default SignIn;
